/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, RefreshCw, AlertTriangle, Lock,
  CalendarRange, BarChart3, Users, ArrowLeftRight, ArrowLeft,
} from "lucide-react";
import FxChip from "@/components/FxChip";
import DataSourcesBanner from "@/components/admin/DataSourcesBanner";

/**
 * AP-5E — Read-only AssosPay admin reports.
 *
 * Three GET endpoints behind `/admin/assospay/reports`:
 *   - daily   `/api/admin/assospay/reports/daily`
 *   - users   `/api/admin/assospay/reports/users`
 *   - transfers `/api/admin/assospay/reports/transfers`
 *
 * Hard rules (mirror the backend contract):
 *   * NO POSTs — only GET to the report endpoints.
 *   * NO `/credit`, `/webhook`, RPC, wallet-signature calls.
 *   * Server is the source of redaction; the UI just renders strings.
 *   * Cursor pagination via opaque `next_cursor`.
 */
const TOKEN_KEY = "arc_admin_token";
const TAB_KEY = "assospay_reports_active_tab";
const VIEW_KEY = "assospay_reports_currency_view";  // Step 4 — EUR/USD/Both

function yyyymmdd(d) {
  return d.toISOString().slice(0, 10);
}
function defaultRange() {
  const to = new Date();
  const from = new Date(to.getTime() - 6 * 24 * 60 * 60 * 1000);
  return [yyyymmdd(from), yyyymmdd(to)];
}

async function authedFetch(path, token) {
  const res = await fetch(`${API}${path}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${text || res.statusText}`);
  }
  return res.json();
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminAssosPayReports() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState(() => localStorage.getItem(TAB_KEY) || "daily");
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "EUR");  // Step 4
  const [fromD, setFromD] = useState(() => defaultRange()[0]);
  const [toD, setToD] = useState(() => defaultRange()[1]);
  const [status, setStatus] = useState("all");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const inFlightRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(TAB_KEY, tab);
  }, [tab]);

  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  const buildPath = useCallback((cursor) => {
    if (tab === "daily") {
      return `/admin/assospay/reports/daily?from=${fromD}&to=${toD}`;
    }
    if (tab === "users") {
      const cursorQ = cursor ? `&cursor=${encodeURIComponent(cursor)}` : "";
      return `/admin/assospay/reports/users?from=${fromD}&to=${toD}&limit=50${cursorQ}`;
    }
    const cursorQ = cursor ? `&cursor=${encodeURIComponent(cursor)}` : "";
    return `/admin/assospay/reports/transfers?from=${fromD}&to=${toD}&status=${status}&limit=50${cursorQ}`;
  }, [tab, fromD, toD, status]);

  const fetchPage = useCallback(async () => {
    if (!authed || !token) return;
    if (inFlightRef.current) return;
    inFlightRef.current = "first";
    setLoading(true);
    try {
      const out = await authedFetch(buildPath(null), token);
      setData(out);
    } catch (e) {
      const msg = String(e.message || "");
      if (msg.startsWith("401") || msg.startsWith("403")) {
        toast.error(`auth rejected: ${msg}`);
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else if (msg.startsWith("400")) {
        toast.error("invalid date range (max span = 92 days)");
      } else {
        toast.error(`load failed: ${msg}`);
      }
    } finally {
      setLoading(false);
      inFlightRef.current = null;
    }
  }, [authed, token, buildPath]);

  useEffect(() => {
    fetchPage();
  }, [tab, fromD, toD, status, fetchPage]);

  const loadMore = async () => {
    if (!data?.page?.next_cursor) return;
    setMoreLoading(true);
    try {
      const out = await authedFetch(buildPath(data.page.next_cursor), token);
      setData((prev) => ({
        ...out,
        rows: [...(prev?.rows || []), ...(out.rows || [])],
      }));
    } catch (e) {
      toast.error(`load more failed: ${e.message}`);
    } finally {
      setMoreLoading(false);
    }
  };

  const onLogin = async () => {
    if (!token) return toast.error("token required");
    try {
      await authedFetch("/admin/assospay/reports/daily?from=2026-01-01&to=2026-01-01", token);
    } catch (e) {
      toast.error(`auth failed: ${e.message}`);
      return;
    }
    localStorage.setItem(TOKEN_KEY, token);
    setAuthed(true);
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setData(null);
  };

  if (!authed) {
    return <LoginShell token={token} setToken={setToken} onLogin={onLogin} />;
  }

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      data-testid="admin-assospay-reports-page"
    >
      <header className="border-b border-white/5 bg-black/60 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Lock className="h-4 w-4 text-[#00F0FF]" />
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
              AssosPay · Operator · Reports
            </p>
            <h1 className="font-display text-xl font-black truncate">
              Admin Reports
            </h1>
          </div>
          <Link
            to="/admin/assospay"
            data-testid="reports-back-to-overview"
            className="inline-flex items-center gap-2 rounded border border-white/15 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            Overview
          </Link>
          <button
            type="button"
            onClick={fetchPage}
            disabled={loading}
            data-testid="reports-refresh-btn"
            className="inline-flex items-center gap-2 rounded border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onLogout}
            data-testid="reports-logout-btn"
            className="inline-flex items-center gap-2 rounded border border-white/15 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/65 hover:bg-white/10 transition-colors"
          >
            Disconnect
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <HardRuleBanner />
        <DataSourcesBanner />

        <DateRangeBar
          fromD={fromD} setFromD={setFromD}
          toD={toD} setToD={setToD}
        />

        <Tabs tab={tab} setTab={setTab} />

        <div className="flex flex-wrap items-center gap-3" data-testid="reports-toolbar-step4">
          <CurrencyToggle view={view} setView={setView} />
          <FxChip snap={data?.fx_chip_snapshot} testid="reports-fx-chip" />
        </div>

        {tab === "transfers" && (
          <TransferStatusFilter status={status} setStatus={setStatus} />
        )}

        {loading && !data && (
          <p data-testid="reports-loading" className="font-mono text-xs text-white/55">
            Loading…
          </p>
        )}
        {data && tab === "daily" && <DailyReport data={data} view={view} />}
        {data && tab === "users" && (
          <UsersReport data={data} loadMore={loadMore} moreLoading={moreLoading} view={view} />
        )}
        {data && tab === "transfers" && (
          <TransfersReport data={data} loadMore={loadMore} moreLoading={moreLoading} view={view} />
        )}
      </main>
    </div>
  );
}

/* ------------------------ shared ------------------------ */
function LoginShell({ token, setToken, onLogin }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4" data-testid="reports-login-shell">
      <div className="w-full max-w-md rounded-md border border-white/10 bg-black/60 p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
          AssosPay · Operator
        </p>
        <h1 className="font-display text-xl font-black mt-1 mb-4">
          Reports · Sign in
        </h1>
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">ARC_ADMIN_TOKEN</label>
        <input
          type="password" autoComplete="off" spellCheck={false}
          value={token} onChange={(e) => setToken(e.target.value)}
          placeholder="paste your admin token"
          data-testid="reports-token-input"
          className="mt-2 w-full rounded border border-white/15 bg-black/40 px-3 py-2 font-mono text-xs text-white outline-none focus:border-[#00F0FF]/60"
        />
        <button
          type="button" onClick={onLogin}
          data-testid="reports-login-btn"
          className="mt-4 inline-flex items-center gap-2 rounded border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors"
        >
          <Lock className="h-3 w-3" /> Enter
        </button>
      </div>
    </div>
  );
}

function HardRuleBanner() {
  return (
    <div data-testid="reports-hard-rule" className="rounded-md border border-[#39FF14]/30 bg-[#39FF14]/5 p-4 flex items-start gap-2">
      <ShieldCheck className="h-4 w-4 text-[#39FF14] mt-0.5" />
      <div className="font-mono text-[11px] text-white/70 leading-relaxed">
        <span className="text-[#39FF14] font-bold">Read-only reports.</span>{" "}
        No writes, no credit, no transfer, no signature, no env mutation.
      </div>
    </div>
  );
}

function DateRangeBar({ fromD, setFromD, toD, setToD }) {
  return (
    <div data-testid="reports-date-range" className="flex flex-wrap items-end gap-3 rounded-md border border-white/10 bg-black/40 p-3">
      <CalendarRange className="h-4 w-4 text-[#00F0FF] mt-2" />
      <DateInput label="From" value={fromD} onChange={setFromD} testid="reports-from-input" />
      <DateInput label="To" value={toD} onChange={setToD} testid="reports-to-input" />
      <span className="font-mono text-[10px] text-white/40 ml-auto">max 92 days · UTC</span>
    </div>
  );
}

function DateInput({ label, value, onChange, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/55">
        {label}
      </span>
      <input
        type="date" value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="rounded border border-white/15 bg-black/40 px-2 py-1 font-mono text-xs text-white outline-none focus:border-[#00F0FF]/60"
      />
    </label>
  );
}

function Tabs({ tab, setTab }) {
  const items = [
    { id: "daily", label: "Daily", icon: BarChart3 },
    { id: "users", label: "Users", icon: Users },
    { id: "transfers", label: "Transfers", icon: ArrowLeftRight },
  ];
  return (
    <nav data-testid="reports-tabs" className="flex flex-wrap gap-2">
      {items.map(({ id, label, icon: Icon }) => {
        const active = tab === id;
        return (
          <button
            key={id} type="button" onClick={() => setTab(id)}
            data-testid={`tab-${id}`}
            data-active={active ? "true" : "false"}
            className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
              active
                ? "border-[#00F0FF]/60 bg-[#00F0FF]/10 text-[#00F0FF]"
                : "border-white/15 bg-white/5 text-white/55 hover:bg-white/10"
            }`}
          >
            <Icon className="h-3 w-3" />
            {label}
          </button>
        );
      })}
    </nav>
  );
}

function TransferStatusFilter({ status, setStatus }) {
  const options = ["all", "completed", "pending", "rejected", "failed"];
  return (
    <div className="flex items-center gap-2" data-testid="reports-status-filter">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">
        Status:
      </span>
      {options.map((o) => {
        const active = status === o;
        return (
          <button
            key={o} type="button" onClick={() => setStatus(o)}
            data-testid={`status-${o}`}
            data-active={active ? "true" : "false"}
            className={`rounded border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
              active
                ? "border-[#00F0FF]/60 bg-[#00F0FF]/10 text-[#00F0FF]"
                : "border-white/15 bg-white/5 text-white/55 hover:bg-white/10"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------ Step 4 — currency toggle + FX chip ------------------------ */
function CurrencyToggle({ view, setView }) {
  const options = ["EUR", "USD", "Both"];
  return (
    <div className="flex items-center gap-2" data-testid="reports-currency-toggle">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">
        View:
      </span>
      {options.map((o) => {
        const active = view === o;
        return (
          <button
            key={o} type="button" onClick={() => setView(o)}
            data-testid={`view-${o.toLowerCase()}`}
            data-active={active ? "true" : "false"}
            className={`rounded border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
              active
                ? "border-[#00F0FF]/60 bg-[#00F0FF]/10 text-[#00F0FF]"
                : "border-white/15 bg-white/5 text-white/55 hover:bg-white/10"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function FxChip_REMOVED_USE_SHARED({ snap }) {  // eslint-disable-line no-unused-vars
  // Replaced by the shared `@/components/FxChip` — kept as a stub for git-history clarity.
  return null;
}

/* ------------------------ daily ------------------------ */
function DailyReport({ data, view }) {
  const t = data.totals || {};
  const showEur = view !== "USD";
  const showUsd = view !== "EUR";
  return (
    <div className="space-y-4" data-testid="report-daily">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <Tile testid="d-total" label="Total" value={t.redemption_requests ?? 0} />
        <Tile testid="d-pending" label="Pending" value={t.pending} accent="amber" />
        <Tile testid="d-completed" label="Completed" value={t.completed} accent="green" />
        <Tile testid="d-rejected" label="Rejected" value={t.rejected} accent="muted" />
        <Tile testid="d-failed" label="Failed" value={t.failed} accent="amber" />
        <Tile testid="d-unique" label="Unique users" value={t.unique_users} />
        <Tile testid="d-arc" label="ARC requested" value={t.arc_requested ?? "0"} />
        {showEur && (
          <Tile testid="d-eur" label="EUR credited" value={`€${t.eur_credited ?? "0"}`} accent="cyan" />
        )}
        {showUsd && (
          <Tile testid="d-usd" label="USD value" value={`$${t.usd_value ?? "0"}`} accent="green" />
        )}
      </div>
      <Table
        testid="report-daily-table"
        cols={[
          "Date", "Requests", "Pending", "Completed", "Rejected", "Failed", "Uniq", "ARC",
          ...(showEur ? ["EUR", "Avg EUR"] : []),
          ...(showUsd ? ["USD", "Peg rows"] : []),
        ]}
        rows={(data.rows || []).map((r) => [
          r.date, r.redemption_requests, r.pending, r.completed,
          r.rejected, r.failed, r.unique_users,
          r.arc_requested,
          ...(showEur ? [`€${r.eur_credited}`, `€${r.avg_eur_per_completed}`] : []),
          ...(showUsd ? [
            r.usd_value && r.usd_value !== "0" ? `$${r.usd_value}` : "—",
            r.usd_peg_v1_rows ?? 0,
          ] : []),
        ])}
        emptyLabel="No activity in this range."
      />
    </div>
  );
}

/* ------------------------ users ------------------------ */
function UsersReport({ data, loadMore, moreLoading, view }) {
  const showEur = view !== "USD";
  const showUsd = view !== "EUR";
  return (
    <div className="space-y-4" data-testid="report-users">
      <Table
        testid="report-users-table"
        cols={[
          "User", "Auth", "Wallet", "Verif.", "Total", "Done", "Pend.", "Reject", "Fail", "ARC",
          ...(showEur ? ["EUR"] : []),
          ...(showUsd ? ["USD", "Last mode", "FX"] : []),
          "Last",
        ]}
        rows={(data.rows || []).map((r) => [
          r.arc_user_id_last4 ? `…${r.arc_user_id_last4}` : "—",
          (r.auth_methods || []).join(", ") || "—",
          r.wallet_address_redacted || "—",
          r.verification_status || "—",
          r.redemption_total, r.completed, r.pending, r.rejected, r.failed,
          r.arc_redeemed,
          ...(showEur ? [`€${r.eur_credited}`] : []),
          ...(showUsd ? [
            r.usd_value && r.usd_value !== "0" ? `$${r.usd_value}` : "—",
            r.last_pricing_mode || "—",
            r.last_fx_label || "—",
          ] : []),
          formatTs(r.last_activity_at),
        ])}
        emptyLabel="No users redeemed in this range."
      />
      {data.page?.next_cursor && (
        <button
          type="button" onClick={loadMore} disabled={moreLoading}
          data-testid="report-users-load-more"
          className="inline-flex items-center gap-2 rounded border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors disabled:opacity-50"
        >
          {moreLoading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

/* ------------------------ transfers ------------------------ */
function TransfersReport({ data, loadMore, moreLoading, view }) {
  const showEur = view !== "USD";
  const showUsd = view !== "EUR";
  return (
    <div className="space-y-4" data-testid="report-transfers">
      <Table
        testid="report-transfers-table"
        cols={[
          "Created", "Ref", "User", "Status", "Branch", "ARC",
          ...(showEur ? ["EUR"] : []),
          ...(showUsd ? ["USD", "Mode", "FX rate"] : []),
          "Rate", "Txn", "Partner", "Mut?",
        ]}
        rows={(data.rows || []).map((r) => {
          const rs = r.rate_snapshot || {};
          const isPeg = rs.pricing_mode === "usd_peg_v1";
          return [
            formatTs(r.created_at),
            <span key="ref" className="font-mono text-[10px] text-white/80">{r.arc_reference_id_redacted || "—"}</span>,
            r.arc_user_id_last4 ? `…${r.arc_user_id_last4}` : "—",
            <StatusChip key="status" status={r.status} />,
            r.pilot_branch || "—",
            r.arc_amount || "—",
            ...(showEur ? [r.eur_amount ? `€${r.eur_amount}` : "—"] : []),
            ...(showUsd ? [
              isPeg && rs.usd_value ? `$${rs.usd_value}` : "—",
              isPeg ? (
                <PegBadge key="mode" stale={rs.fx_stale} />
              ) : (
                <span key="mode" className="font-mono text-[10px] text-white/40">legacy</span>
              ),
              isPeg && rs.eur_per_usd ? `${rs.eur_per_usd} ${rs.fx_label ? `(${rs.fx_label})` : ""}` : "—",
            ] : []),
            rs.label || "—",
            r.assos365_transaction_id_redacted || "—",
            r.partner_status || "—",
            r.balance_mutated ? "yes" : "no",
          ];
        })}
        emptyLabel="No transfers in this range."
      />
      {data.page?.next_cursor && (
        <button
          type="button" onClick={loadMore} disabled={moreLoading}
          data-testid="report-transfers-load-more"
          className="inline-flex items-center gap-2 rounded border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors disabled:opacity-50"
        >
          {moreLoading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

function PegBadge({ stale }) {
  return (
    <span
      data-testid="row-peg-badge"
      data-stale={stale ? "true" : "false"}
      className={`inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${
        stale
          ? "text-[#FFB800] border-[#FFB800]/40"
          : "text-[#39FF14] border-[#39FF14]/40"
      }`}
    >
      usd_peg_v1{stale ? " · STALE" : ""}
    </span>
  );
}

function StatusChip({ status }) {
  const palette = {
    fulfilled: "text-[#39FF14] border-[#39FF14]/40",
    submitted: "text-[#FFB800] border-[#FFB800]/40",
    pending_partner_review: "text-[#FFB800] border-[#FFB800]/40",
    rejected: "text-white/55 border-white/20",
    failed_terminal: "text-[#FF3B30] border-[#FF3B30]/40",
    failed_retryable: "text-[#FFB800] border-[#FFB800]/40",
  }[status] || "text-white/55 border-white/15";
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${palette}`}>
      {status || "—"}
    </span>
  );
}

/* ------------------------ primitives ------------------------ */
function Tile({ label, value, accent, testid }) {
  const palette = {
    cyan: "text-[#00F0FF]",
    green: "text-[#39FF14]",
    amber: "text-[#FFB800]",
    muted: "text-white/75",
  }[accent || "muted"];
  return (
    <div data-testid={testid} className="rounded border border-white/10 bg-black/40 p-3">
      <div className="text-[9px] uppercase tracking-[0.2em] text-white/45">{label}</div>
      <div className={`mt-1 font-display text-base font-black ${palette}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Table({ cols, rows, emptyLabel, testid }) {
  if (!rows || rows.length === 0) {
    return (
      <div data-testid={`${testid}-empty`} className="font-mono text-[11px] text-white/45 italic">
        {emptyLabel}
      </div>
    );
  }
  return (
    <div data-testid={testid} className="overflow-x-auto rounded-md border border-white/5 bg-black/40">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-white/5">
            {cols.map((c, i) => (
              <th key={i} className="px-3 py-2 font-mono text-[9px] uppercase tracking-[0.18em] text-white/45">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 font-mono text-[11px] text-white/80">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
